import { Button, Checkbox, DatePicker, TextInput, Tile } from '@Components/common';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './KeyPersonForm.module.scss';
import { RiArrowLeftLine, RiDeleteBinLine } from 'react-icons/ri';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { KeyPersonRoleSelect } from './Components/KeyPersonRoleSelect';
import { KeyPerson, KeyPersonRole } from '@Types/KeyPeople';
import { AddressSection, SectionHeader } from '@Components';
import { msgFieldRequired } from '@Helpers';
import classNames from 'classnames';

export const KeyPersonForm = ({
  closeForm,
  keyPeopleRoles,
  setKeyPeople,
  keyPeople,
  setKeyPerson,
  keyPerson,
}: {
  closeForm: () => void;
  keyPeopleRoles: KeyPersonRole[];
  setKeyPeople: (keyPeople: KeyPerson[]) => void;
  keyPeople: KeyPerson[];
  setKeyPerson: (keyPerson: KeyPerson | undefined) => void;
  keyPerson?: KeyPerson;
}) => {
  const methods = useForm({ defaultValues: keyPerson || {} });
  const { is_representative: isRepresentative } = useWatch({
    control: methods.control,
    name: ['is_owner', 'is_representative'],
  });

  // Stored in a state to only render on initial load, otherwise when editing a current user
  // and adding them as a rep would automatically tick the terms boxes which we don't want
  const [hasAcceptedRepresentativeTerms, setHasAcceptedRepresentativeTerms] = useState(
    isRepresentative && keyPerson,
  );
  const { t } = useTranslation('account');

  const resetAndCloseForm = useCallback(() => {
    setKeyPerson(undefined);
    closeForm();
  }, [closeForm, setKeyPerson]);

  const onSubmit = methods.handleSubmit(
    // if keyPerson exists in keypeople update the existing entry if not add it as a new keyPerson
    (data) => {
      const newKeyPerson: KeyPerson = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        date_of_birth: data.date_of_birth,
        is_representative: data.is_representative || false,
        is_owner: data.is_owner || false,
        is_director: data.is_director || false,
        is_executive: data.is_executive || false,
        address1: data.address1,
        address2: data.address2,
        address3: data.address3,
        city: data.city,
        county: data.county,
        postcode: data.postcode,
      };

      if (keyPerson) {
        const updatedKeyPeople = keyPeople.map((person) =>
          person === keyPerson ? newKeyPerson : person,
        );
        setKeyPeople(updatedKeyPeople);
      } else {
        setKeyPeople([...keyPeople, newKeyPerson]);
      }
      resetAndCloseForm();
    },
    () => {},
  );

  const handleRemoveKeyPerson = useCallback(() => {
    const updatedKeyPeople = keyPeople.filter((person) => person !== keyPerson);
    setKeyPeople(updatedKeyPeople);
    resetAndCloseForm();
  }, [keyPeople, setKeyPeople, resetAndCloseForm, keyPerson]);

  const roleSelectRequired = () => {
    return !!(
      methods.errors.is_owner &&
      methods.errors.is_representative &&
      methods.errors.is_director &&
      methods.errors.is_executive
    );
  };

  // Temp saving variable waiting for api call for this
  const isSaving = false;

  return (
    <>
      <Button
        buttonType="text"
        onClick={() => {
          resetAndCloseForm();
        }}
        startIcon={<RiArrowLeftLine />}
      >
        {t('back')}
      </Button>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className={styles.formContainer}>
          <Tile
            title={{
              title: !keyPerson
                ? t('tabs.organization.add_key_people.title')
                : t('tabs.organization.add_key_people.edit_title'),
            }}
            className={styles.container}
          >
            <div className={styles.container}>
              <div>
                <SectionHeader
                  title={t('tabs.organization.add_key_people.roles')}
                  subtitle={t('tabs.organization.add_key_people.roles_description')}
                />
                <div
                  className={classNames(
                    styles.roleSelect,
                    roleSelectRequired() && styles.roleSelectError,
                  )}
                >
                  {keyPeopleRoles.map((role) => (
                    <Controller
                      key={role.name}
                      name={`is_${role.name.toLowerCase()}`}
                      control={methods.control}
                      render={({ value, onChange, ref }) => {
                        return (
                          <KeyPersonRoleSelect
                            inputRef={ref}
                            value={ref.current.checked}
                            checked={Boolean(value)}
                            name={role.name}
                            description={role.description}
                            onChange={(e) => {
                              onChange(e.target.checked);
                            }}
                            required={roleSelectRequired()}
                            disabled={
                              role.name.toLowerCase() === 'representative' &&
                              keyPeople.some((person) => person.is_representative)
                            }
                          />
                        );
                      }}
                    />
                  ))}
                </div>
                {roleSelectRequired() && (
                  <p className={styles.roleSelectErrorText}>
                    {t('tabs.organization.add_key_people.roles_required')}
                  </p>
                )}
              </div>

              <div>
                <SectionHeader
                  title={t('tabs.organization.add_key_people.basic_information.title')}
                />
                <div className={styles.nameContainer}>
                  <TextInput
                    label={t('tabs.organization.add_key_people.basic_information.first_name')}
                    inputRef={methods.register({ required: msgFieldRequired })}
                    name="first_name"
                    error={!!methods.errors.first_name}
                    helperText={methods.errors.first_name?.message}
                    disabled={isSaving}
                  />
                  <TextInput
                    label={t('tabs.organization.add_key_people.basic_information.last_name')}
                    inputRef={methods.register({ required: msgFieldRequired })}
                    name="last_name"
                    error={!!methods.errors.last_name}
                    helperText={methods.errors.last_name?.message}
                    disabled={isSaving}
                  />
                </div>
                <DatePicker
                  name="date_of_birth"
                  required={true}
                  label={t('tabs.organization.add_key_people.basic_information.date_of_birth')}
                  error={!!methods.errors.date_of_birth}
                  helperText={methods.errors.date_of_birth?.message}
                  disabled={isSaving}
                  openTo="year"
                />
                <TextInput
                  label={t('tabs.organization.add_key_people.basic_information.email')}
                  inputRef={methods.register({ required: msgFieldRequired })}
                  name="email"
                  error={!!methods.errors.email}
                  helperText={methods.errors.email?.message}
                  disabled={isSaving}
                />
              </div>

              <div>
                <SectionHeader title={t('tabs.organization.add_key_people.home_address')} />
                <AddressSection isLoading={false} />
              </div>

              {isRepresentative && (
                <div>
                  <SectionHeader
                    title={t('tabs.organization.add_key_people.legal_declerations.title')}
                  />

                  <Controller
                    name="responsible_terms_accepted"
                    control={methods.control}
                    rules={{ required: msgFieldRequired }}
                    defaultValue={hasAcceptedRepresentativeTerms}
                    render={({ ref, onChange, value }) => (
                      <Checkbox
                        inputRef={ref}
                        value={ref.current.checked}
                        checked={Boolean(value)}
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                        error={!!methods.errors.responsible_terms_accepted}
                        helperText={methods.errors.responsible_terms_accepted?.message}
                        disabled={isSaving}
                      >
                        {t('tabs.organization.add_key_people.legal_declerations.is_responsible')}
                      </Checkbox>
                    )}
                  />

                  <Controller
                    name="trustee_terms_accepted"
                    control={methods.control}
                    rules={{ required: msgFieldRequired }}
                    defaultValue={hasAcceptedRepresentativeTerms}
                    render={({ ref, onChange, value }) => (
                      <Checkbox
                        inputRef={ref}
                        value={ref.current.checked}
                        checked={Boolean(value)}
                        onChange={(e) => {
                          onChange(e.target.checked);
                        }}
                        error={!!methods.errors.trustee_terms_accepted}
                        helperText={methods.errors.trustee_terms_accepted?.message}
                        disabled={isSaving}
                      >
                        {t('tabs.organization.add_key_people.legal_declerations.is_trustee')}
                      </Checkbox>
                    )}
                  />
                </div>
              )}
            </div>
          </Tile>
          <div className={styles.buttonsContainer}>
            {keyPerson && (
              <Button
                buttonType="danger"
                className={styles.deletePersonButton}
                startIcon={<RiDeleteBinLine />}
                onClick={handleRemoveKeyPerson}
              >
                {t('tabs.organization.add_key_people.remove_person')}
              </Button>
            )}
            <Button buttonType="primary" type="submit" className={styles.button}>
              {keyPerson
                ? t('tabs.organization.add_key_people.update_person')
                : t('tabs.organization.add_key_people.add_person')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
